import { type Dispatch, type SetStateAction, useState } from "react";
import { useHistory } from "react-router-dom";
import type { Moment } from "moment";
import type {
    NewClassFormState,
    InviteModalState,
} from "../complex/classActivityCard/ClassActivityCardsOverview.types";
import { ShareTimings } from "../enums/shareFlow";
import { collapseClassForm } from "../helpers/ShareFlow";
import { getClassDetailsRoute } from "../helpers/navigationHelper";

export interface ShareFlowSubState {
    selectedSchoolClassId: string | null;
    newClassForm: NewClassFormState | null;
    timing: ShareTimings;
    visibilityDate: Moment | null;
}

export const useShareFlowState = <S extends ShareFlowSubState>(setSharePopup: Dispatch<SetStateAction<S>>) => {
    const history = useHistory();

    const [shareInProgress, setShareInProgress] = useState(false);
    const [inviteModal, setInviteModal] = useState<InviteModalState | null>(null);

    const handleCloseInviteModal = () => setInviteModal(null);

    const handleInviteNavigate = () => {
        if (!inviteModal) return;
        history.push(getClassDetailsRoute(inviteModal.schoolClassId));
        setInviteModal(null);
    };

    const handleSelectShareClass = (schoolClassId: string | null) => {
        setSharePopup((prev) => ({
            ...prev,
            selectedSchoolClassId: prev.selectedSchoolClassId === schoolClassId ? null : schoolClassId,
            newClassForm: null,
        }));
    };

    const handleCloseNewClassForm = () => {
        setSharePopup((prev) => (prev.newClassForm ? { ...prev, newClassForm: null } : prev));
    };

    const handleCollapseNewClassForm = () => {
        setSharePopup((prev) =>
            prev.newClassForm ? { ...prev, newClassForm: collapseClassForm(prev.newClassForm, false) } : prev
        );
    };

    const handleNewClassNameChange = (name: string) => {
        setSharePopup((prev) => (prev.newClassForm ? { ...prev, newClassForm: { ...prev.newClassForm, name } } : prev));
    };

    const handleToggleNewClassConsent = () => {
        setSharePopup((prev) =>
            prev.newClassForm
                ? {
                      ...prev,
                      newClassForm: { ...prev.newClassForm, hasConsent: !prev.newClassForm.hasConsent },
                  }
                : prev
        );
    };

    const handleSelectShareTiming = (timing: ShareTimings) => {
        setSharePopup((prev) => ({
            ...prev,
            timing,
            visibilityDate: timing === ShareTimings.LATER ? prev.visibilityDate : null,
        }));
    };

    const handleShareVisibilityDateChange = (value: Moment | null) => {
        setSharePopup((prev) => ({ ...prev, visibilityDate: value }));
    };

    return {
        shareInProgress,
        setShareInProgress,
        inviteModal,
        setInviteModal,
        handleCloseInviteModal,
        handleInviteNavigate,
        handleSelectShareClass,
        handleCloseNewClassForm,
        handleCollapseNewClassForm,
        handleNewClassNameChange,
        handleToggleNewClassConsent,
        handleSelectShareTiming,
        handleShareVisibilityDateChange,
    };
};
